import { themeAtom } from "../../shared/atoms";
import CronJobCard from "../components/debug/CronJobCard";
import TechFormInboxDebugPanel from "../components/debug/TechFormInboxDebugPanel";
import type { AdminCronJob } from "../models/admin-cron.models";
import {
  CRON_JOB_LOG_ENVIRONMENTS,
  type CronJobLogEnvironment,
} from "../models/admin-monitoring.models";
import { adminStyles } from "../styles/admin.styles";
import { getDefaultAdminMonitoringEnvironment } from "../utils/admin-monitoring-environment.utils";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

const TECH_FORM_INBOX_IMPORT_JOB: AdminCronJob = {
  id: "offline-tech-form-inbox-import-debug",
  group: "Forms",
  title: "Targeted inbox import",
  purpose: "Imports one vessel's matching Tech Form return payload through the existing debug-only save flow.",
  outputFocus: "Matched email, attachment payload, Tech Form submission update, and stored-procedure save result.",
  params: [
    {
      key: "imoNumber",
      label: "IMO number",
      type: "text",
      placeholder: "9875642",
      helper: "Required to limit the import to one vessel.",
    },
    {
      key: "mailbox",
      label: "Mailbox to scan",
      type: "email",
      defaultValue: "Ot-geneva_Auto-form-sending@cargill.com",
      placeholder: "Ot-geneva_Auto-form-sending@cargill.com",
      helper: "Shared return mailbox printed in the offline Tech Form email.",
    },
  ],
  variants: [
    {
      id: "read-inbox",
      label: "Import inbox",
      endpoint: "read-tech-form-inbox-by-imos-num-and-email-dev",
      risk: "verification",
      description:
        "Imports matching Tech Form JSON, updates the submission, and runs the existing stored-procedure save flow.",
      paramKeys: ["imoNumber", "mailbox"],
      requiredParamKeys: ["imoNumber", "mailbox"],
    },
  ],
};

// Keeps mailbox reads and imports on one dedicated Debug page.
// Business-role monitoring pages must never render these dev-team-only tools.
const AdminTechFormInboxDebugPage = () => {
  const theme = useAtomValue(themeAtom);
  const [environment, setEnvironment] = useState<CronJobLogEnvironment>(getDefaultAdminMonitoringEnvironment);

  return (
    <main
      data-test="admin-tech-form-inbox-debug-page"
      className={twMerge("min-h-0 flex-1 overflow-auto", adminStyles.page.pagePadding)}
    >
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase text-green">Debug</p>
          <h1 className={twMerge("mt-1", adminStyles.heading.pageTitle, theme.home.tailwind.textDefault)}>
            Tech Form Inbox
          </h1>
          <p className={twMerge("mt-2", adminStyles.heading.body, theme.uwhcAssistant.textOutput)}>
            Dev-team-only mailbox inspection and controlled Tech Form payload recovery.
          </p>
        </div>

        <label className="text-xs font-semibold uppercase" data-test="admin-tech-form-inbox-environment-control">
          <span className="mb-1 block opacity-70">Environment</span>
          <select
            value={environment}
            data-test="admin-tech-form-inbox-environment-select"
            className="h-10 border border-gray-300 bg-transparent px-3 text-sm normal-case outline-none focus:border-green dark:border-gray-700"
            onChange={(event) => setEnvironment(event.target.value as CronJobLogEnvironment)}
          >
            {CRON_JOB_LOG_ENVIRONMENTS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="space-y-6">
        <TechFormInboxDebugPanel environment={environment} />
        <div className="max-w-3xl" data-test="admin-tech-form-inbox-import-tool">
          <CronJobCard job={TECH_FORM_INBOX_IMPORT_JOB} />
        </div>
      </div>
    </main>
  );
};

export default AdminTechFormInboxDebugPage;
